import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchVehicleDamageReports, createVehicleDamageReport } from "../../../../api/vehicles/damage";
import VehicleDamageProcessDialog from "./vehicle-damage-process-dialog";

export const VehicleDamagePanel = ({ vehicleId, canManage = true }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [processingReport, setProcessingReport] = useState(null);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchVehicleDamageReports({
          vehicle_id: vehicleId,
          filters: [{ name: "vehicle_id", value: vehicleId }],
          per_page: 50,
          sortBy: "occurred_at",
          sort: "DESC",
        });
        if (res && typeof res === "object" && res.errors && !Array.isArray(res.data)) {
          setError("Failed to load damage reports");
          setReports([]);
          return;
        }
        const list = Array.isArray(res?.data) ? res.data : [];
        setReports(list);
      } catch (e) {
        setError("Failed to load damage reports");
      } finally {
        setIsLoading(false);
      }
    }
    if (vehicleId) load();
  }, [vehicleId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const form = e.target;
    const payload = {
      vehicle_id: vehicleId,
      occurred_at: form.occurred_at.value,
      description: form.description.value,
      location: form.location.value,
    };
    const res = await createVehicleDamageReport(payload);
    if (res?.id) {
      setReports([res, ...reports]);
      setShowDialog(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("Damage Reports")}</h3>
          <p className="text-sm text-gray-500">{t("Report damages for this vehicle")}</p>
        </div>
        {canManage && (
          <button onClick={() => setShowDialog(true)} className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500">
            {t("New Report")}
          </button>
        )}
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="text-sm text-gray-500">{t("Loading...")}</div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : reports.length === 0 ? (
            <div className="text-sm text-gray-500">{t("No damage reports yet")}</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {reports.map((r) => (
                <li key={r.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">{r.occurred_at ? new Date(r.occurred_at).toLocaleDateString() : ""}</div>
                    <div className="text-sm text-gray-500">{r.location || "-"}</div>
                  </div>
                  {r.description && <div className="text-sm text-gray-700 mt-1">{r.description}</div>}
                  {canManage && !r.processed && (
                    <div className="mt-2">
                      <button onClick={() => setProcessingReport(r)} className="px-2 py-1 rounded bg-gray-800 text-white text-xs">{t("Process")}</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {canManage && showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <form onSubmit={handleCreate} className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="text-lg font-semibold mb-4">{t("New Damage Report")}</div>
            <div className="space-y-3">
              <input name="occurred_at" type="date" className="w-full border rounded px-3 py-2" required />
              <input name="location" className="w-full border rounded px-3 py-2" placeholder={t("Location (optional)")} />
              <textarea name="description" className="w-full border rounded px-3 py-2" placeholder={t("Description")} required />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowDialog(false)} className="px-3 py-2 rounded border">{t("Cancel")}</button>
              <button type="submit" className="px-3 py-2 rounded bg-blue-600 text-white">{t("Save")}</button>
            </div>
          </form>
        </div>
      )}
      {canManage && processingReport && (
        <VehicleDamageProcessDialog
          open={!!processingReport}
          onClose={() => setProcessingReport(null)}
          report={processingReport}
          vehicleId={vehicleId}
          onUpdated={(updated) => {
            setReports(reports.map((x) => (x.id === updated.id ? updated : x)));
          }}
        />
      )}
    </div>
  );
};

export default VehicleDamagePanel;


